import { ConfigurationItem } from "@shared/types";
import {
  addUuidToConfigurationItems,
  convertValues,
  getAllConfigurationItems,
  getChildren,
  getItemByName as getItemByNameInner,
  getUuidToItems,
  itemShouldDisplay,
} from "@shared/features/configuration/utils";
import { useEffect, useMemo, useReducer, useState } from "react";
import { cloneDeep } from "lodash";

interface useGeneralConfigurationProps {
  configurationItem?: ConfigurationItem;
}

const useSharedGeneralConfiguration = ({
  configurationItem,
}: useGeneralConfigurationProps) => {
  "use no memo";

  const configurationWithUuid = useMemo(() => {
    const clonedConfiguration = cloneDeep(configurationItem);
    addUuidToConfigurationItems(clonedConfiguration);
    return clonedConfiguration;
  }, [configurationItem]);
  const [updateValue, forceUpdate] = useReducer((x) => x + 1, 0);
  const [itemsModified, setItemsModified] = useState<string[]>([]);
  const [clonedConfiguration, setClonedConfiguration] = useState(() =>
    cloneDeep(configurationWithUuid)
  );
  const [configurationId, setConfigurationId] = useState(0);

  // This object contains the original (unmodified) items.
  const [uuidToOriginalItem, setUuidToOriginalItem] = useState(() =>
    getUuidToItems(configurationWithUuid)
  );
  const children = getChildren(clonedConfiguration);

  const idToItem = getAllConfigurationItems(clonedConfiguration);

  const cancelChanges = () => {
    setClonedConfiguration(cloneDeep(configurationWithUuid));
    setItemsModified([]);
    setUuidToOriginalItem(getUuidToItems(configurationWithUuid));
    setConfigurationId(configurationId + 1);
  };

  useEffect(() => {
    cancelChanges();
  }, [configurationWithUuid]);

  const toggleItemModified = (itemUuid: string, isModified: boolean) => {
    const itemPreviouslyModified = itemsModified.includes(itemUuid);
    if (isModified && !itemPreviouslyModified) {
      setItemsModified([...itemsModified, itemUuid]);
    } else if (!isModified && itemPreviouslyModified) {
      setItemsModified(
        itemsModified.filter(
          (modifiedItemUuid) => modifiedItemUuid !== itemUuid
        )
      );
    }
    forceUpdate();
  };

  const convertedConfiguration = useMemo(() => {
    const configuration = cloneDeep(clonedConfiguration);
    convertValues(
      configuration,
      cloneDeep(idToItem),
      Object.values(uuidToOriginalItem)
    );
    return configuration;
  }, [clonedConfiguration, updateValue]);

  const getItemByName = (itemName: string) => {
    return getItemByNameInner(itemName, convertedConfiguration);
  };

  const getItemValueByName = (itemName: string) => {
    return getItemByName(itemName)?.value;
  };

  const getItemShouldDisplay = (item: ConfigurationItem) => {
    return itemShouldDisplay(item, idToItem);
  };

  return {
    getItemValueByName,
    getItemShouldDisplay,
    uuidToOriginalItem,
    toggleItemModified,
    idToItem,
    getItemByName,
    modifiedConfiguration: convertedConfiguration,
    itemsModified,
    setItemsModified,
    cancelChanges,
    configurationId,
    children,
    clonedConfiguration,
    updateConfiguration: forceUpdate,
  };
};

export default useSharedGeneralConfiguration;
